/* eslint-disable react-refresh/only-export-components, max-lines */
import {
  DimeAvatar,
  DimeAvatarBadge,
  DimeAvatarFallback,
  DimeAvatarGroup,
  DimeAvatarGroupCount,
  DimeAvatarImage,
  DimeButton,
  DimeDropdownMenu,
  DimeDropdownMenuContent,
  DimeDropdownMenuItem,
  DimeDropdownMenuSeparator,
  DimeDropdownMenuTrigger,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import { PlusIcon } from 'lucide-react';
import * as React from 'react';

export const Route = createFileRoute('/design-system/avatar')({
  component: AvatarDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-border bg-background p-6">
      {children}
    </div>
  );
}

function AvatarDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Avatar</h1>
      <p className="mb-8 text-muted-foreground">
        A circular image component with fallback initials. Supports badges, groups, size variants,
        and dropdown composition. Built on{' '}
        <code className="font-mono text-sm">@radix-ui/react-avatar</code>.
      </p>

      <DocsSection title="Basic">
        <Preview>
          <DimeAvatar>
            <DimeAvatarImage
              src="https://github.com/shadcn.png"
              alt="@shadcn"
              className="grayscale"
            />
            <DimeAvatarFallback>CN</DimeAvatarFallback>
          </DimeAvatar>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">src</code> and <code className="font-mono">alt</code> to{' '}
          <code className="font-mono">DimeAvatarImage</code>. Always include{' '}
          <code className="font-mono">DimeAvatarFallback</code> — it shows while the image loads or
          if it fails.
        </p>
      </DocsSection>

      <DocsSection title="Badge">
        <Preview>
          <DimeAvatar>
            <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
            <DimeAvatarFallback>CN</DimeAvatarFallback>
            <DimeAvatarBadge className="bg-green-600 dark:bg-green-800" />
          </DimeAvatar>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeAvatarBadge</code> renders a small indicator dot in the
          bottom-right corner. Use <code className="font-mono">className</code> to set the colour —
          e.g. green for online, red for busy.
        </p>
      </DocsSection>

      <DocsSection title="Badge with icon">
        <Preview>
          <DimeAvatar className="grayscale">
            <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
            <DimeAvatarFallback>CN</DimeAvatarFallback>
            <DimeAvatarBadge>
              <PlusIcon />
            </DimeAvatarBadge>
          </DimeAvatar>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass any lucide icon as a child of <code className="font-mono">DimeAvatarBadge</code> —
          the badge sizes and colours it automatically.
        </p>
      </DocsSection>

      <DocsSection title="Avatar group">
        <Preview>
          <DimeAvatarGroup className="grayscale">
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <DimeAvatarFallback>CN</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/maxleiter.png" alt="@maxleiter" />
              <DimeAvatarFallback>ML</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/evilrabbit.png" alt="@evilrabbit" />
              <DimeAvatarFallback>ER</DimeAvatarFallback>
            </DimeAvatar>
          </DimeAvatarGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Wrap avatars in <code className="font-mono">DimeAvatarGroup</code> to overlap them with a
          negative margin stack. Pass <code className="font-mono">className</code> to apply shared
          styles like <code className="font-mono">grayscale</code>.
        </p>
      </DocsSection>

      <DocsSection title="Avatar group with count">
        <Preview>
          <DimeAvatarGroup className="grayscale">
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <DimeAvatarFallback>CN</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/maxleiter.png" alt="@maxleiter" />
              <DimeAvatarFallback>ML</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/evilrabbit.png" alt="@evilrabbit" />
              <DimeAvatarFallback>ER</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatarGroupCount>+3</DimeAvatarGroupCount>
          </DimeAvatarGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Add <code className="font-mono">DimeAvatarGroupCount</code> at the end of a group to show
          how many additional members are not shown.
        </p>
      </DocsSection>

      <DocsSection title="Avatar group with icon count">
        <Preview>
          <DimeAvatarGroup className="grayscale">
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <DimeAvatarFallback>CN</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/maxleiter.png" alt="@maxleiter" />
              <DimeAvatarFallback>ML</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatarGroupCount>
              <PlusIcon />
            </DimeAvatarGroupCount>
          </DimeAvatarGroup>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass an icon as a child of <code className="font-mono">DimeAvatarGroupCount</code> for an
          add-member affordance.
        </p>
      </DocsSection>

      <DocsSection title="Sizes">
        <Preview>
          <div className="flex flex-wrap items-center gap-2 grayscale">
            <DimeAvatar size="sm">
              <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <DimeAvatarFallback>CN</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar>
              <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <DimeAvatarFallback>CN</DimeAvatarFallback>
            </DimeAvatar>
            <DimeAvatar size="lg">
              <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
              <DimeAvatarFallback>CN</DimeAvatarFallback>
            </DimeAvatar>
          </div>
        </Preview>
        <table className="mt-4 w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-muted-foreground">
              <th className="py-2 pr-4 font-medium">Size</th>
              <th className="py-2 pr-4 font-medium">px</th>
              <th className="py-2 font-medium">Description</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                {
                  value: 'sm',
                  px: '24',
                  description: 'Compact — use inside dense lists or table cells.',
                },
                {
                  value: 'default',
                  px: '32',
                  description: 'Standard — most menus and comment threads.',
                },
                {
                  value: 'lg',
                  px: '40',
                  description: 'Prominent — profile headers and detail panels.',
                },
              ] as const
            ).map((row) => (
              <tr key={row.value} className="border-b border-border">
                <td className="py-2 pr-4 font-mono text-xs">{row.value}</td>
                <td className="py-2 pr-4 text-muted-foreground">{row.px}</td>
                <td className="py-2 text-muted-foreground">{row.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </DocsSection>

      <DocsSection title="Dropdown">
        <Preview>
          <DimeDropdownMenu>
            <DimeDropdownMenuTrigger asChild>
              <DimeButton
                variant="ghost"
                size="icon"
                className="rounded-full"
                aria-label="Account menu"
              >
                <DimeAvatar>
                  <DimeAvatarImage src="https://github.com/shadcn.png" alt="@shadcn" />
                  <DimeAvatarFallback>CN</DimeAvatarFallback>
                </DimeAvatar>
              </DimeButton>
            </DimeDropdownMenuTrigger>
            <DimeDropdownMenuContent className="w-32" align="end">
              <DimeDropdownMenuItem>Profile</DimeDropdownMenuItem>
              <DimeDropdownMenuItem>Billing</DimeDropdownMenuItem>
              <DimeDropdownMenuItem>Settings</DimeDropdownMenuItem>
              <DimeDropdownMenuSeparator />
              <DimeDropdownMenuItem variant="destructive">Log out</DimeDropdownMenuItem>
            </DimeDropdownMenuContent>
          </DimeDropdownMenu>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeAvatar</code> inside a{' '}
          <code className="font-mono">DimeDropdownMenuTrigger asChild</code> button for an account
          menu. Set <code className="font-mono">align="end"</code> on the content so it anchors to
          the right.
        </p>
      </DocsSection>
    </div>
  );
}
